// §6.26 — a single-broadcast drilldown export is named after the campaign.
//
// Rule (dossier §6.26, verified against `contracts-legacy/notifications-backend/
// openapi.yaml` §export/start): the export worker names the artifact after the
// campaign ONLY when ALL of these hold:
//   1. exactly ONE distinct broadcast id across `filters.attributedBroadcastIds`
//      plus `attributedBroadcastId`;
//   2. `entityType` is `fundraiser-drilldown` (noun `Donations`) or `subscriptions`
//      (noun `Subscriptions`);
//   3. `filters.broadcastName` is a non-blank string.
// A multi-select filter (or any unmet condition) keeps the generic label.
//
// The legacy executable contract does NOT model this shape — the filename is built
// inside the worker's S3 put, which its own CLOSURE (R8) deliberately leaves
// unmodelled, and `export/start`'s `filters` is free-form. This module is
// therefore the CREATE the issue write set names: a strict input schema plus a
// pure, side-effect-free derivation over it (the PE-1959 contracts-package
// pattern — no I/O, no SDK, deterministic given its inputs), so the ported
// export worker and the dashboard can agree on the naming rule from one place.
//
// Derived pins stay AS WIDE AS THE INPUT (the D14–D21 lesson): `broadcastName` is
// any string here, with blankness being part of the RULE (checked by the
// derivation), not the shape — the legacy `filters` blob never type-checks it.

import { z } from 'zod';

/** The only two entity types the campaign-naming rule applies to (dossier §6.26). */
export const broadcastExportEntityTypeSchema = z.enum(['fundraiser-drilldown', 'subscriptions']);

export type BroadcastExportEntityType = z.infer<typeof broadcastExportEntityTypeSchema>;

/** The noun the export file uses for each entity type — `Donations` or `Subscriptions`. */
export const BROADCAST_EXPORT_LABELS = {
  'fundraiser-drilldown': 'Donations',
  subscriptions: 'Subscriptions',
} as const satisfies Record<BroadcastExportEntityType, string>;

export type BroadcastExportLabel = (typeof BROADCAST_EXPORT_LABELS)[BroadcastExportEntityType];

/**
 * The inputs the naming rule reads: the job's `entityType` plus the
 * `filters` slice carrying the campaign attribution. Both id spellings are
 * optional and may coexist — the legacy filter object is free-form, so the schema
 * must not assume either spelling is present or exclusive.
 */
export const broadcastExportContextSchema = z
  .object({
    entityType: broadcastExportEntityTypeSchema,
    broadcastName: z.string(),
    attributedBroadcastId: z.string().optional(),
    attributedBroadcastIds: z.array(z.string()).optional(),
  })
  .strict();

export type BroadcastExportContext = z.infer<typeof broadcastExportContextSchema>;

/**
 * Exactly ONE distinct broadcast id across both spellings (dossier §6.26:
 * "exactly one distinct id across `filters.attributedBroadcastIds` +
 * `attributedBroadcastId`"). Absent spellings contribute nothing.
 */
const countDistinctBroadcastIds = (ctx: BroadcastExportContext): number => {
  const ids = [...(ctx.attributedBroadcastIds ?? []), ...(ctx.attributedBroadcastId !== undefined ? [ctx.attributedBroadcastId] : [])];
  return new Set(ids).size;
};

/** The naming rule's precondition, as a predicate over the context schema. */
export function isSingleBroadcastExport(ctx: BroadcastExportContext): boolean {
  return countDistinctBroadcastIds(ctx) === 1 && ctx.broadcastName.trim().length > 0;
}

/**
 * The campaign-named label for the artifact filename, or `null` when the naming
 * rule does not apply and the export keeps its generic label. Pure — the same
 * context always yields the same label.
 */
export function broadcastExportLabel(ctx: BroadcastExportContext): BroadcastExportLabel | null {
  if (!isSingleBroadcastExport(ctx)) return null;
  return BROADCAST_EXPORT_LABELS[ctx.entityType];
}
